// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  memo,
  SyntheticEvent,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import styled from '@emotion/styled';
import { DateRange, RangeKeyDict, Range } from 'react-date-range';
const { ja, enUS } = require('react-date-range/dist/locale'); // importするとtypesがなくて怒られるのでrequireで読み込み

import TextField from 'presentations/views/components/atoms/TextField';
import Icon from 'presentations/views/components/atoms/Icon';
import Popover from 'presentations/views/components/atoms/Popover';
import {
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  colorPalette,
  gray01,
  gray02,
  gray03,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { DateTime, formatToYMD } from 'infras/datetime';
import Link from 'presentations/views/components/atoms/Link';

export interface Props {
  size?: 'small' | 'medium' | undefined;
  startedAt?: DateTime;
  endedAt?: DateTime;
  onChange: (startedAt?: Date, endedAt?: Date) => void;
  locale?: string;
  timeZone: string;
  placeholder?: string;
  textFieldHeight?: number;
  textFieldWidth?: number;
  initializedDateRange?: boolean;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);

  const isTablet = isMediaMax('tablet');

  const containerRef = useRef<any>(null);
  const [anchor, setAnchor] = useState<any>(null);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [range, setRange] = useState<Range>(
    props.startedAt && props.endedAt
      ? {
          startDate: props.startedAt.toJSDate(),
          endDate: props.endedAt.toJSDate(),
          key: 'selection',
        }
      : {
          startDate: new Date(),
          endDate: new Date(),
          key: 'selection',
        },
  );

  useEffect(() => {
    if (!props.initializedDateRange) return;

    setRange(
      props.startedAt && props.endedAt
        ? {
            startDate: props.startedAt.toJSDate(),
            endDate: props.endedAt.toJSDate(),
            key: 'selection',
          }
        : {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
          },
    );
    setIsSelected(!!props.startedAt && !!props.endedAt);
  }, [props.initializedDateRange]);

  useEffect(() => {
    if (!props.startedAt && !props.endedAt) {
      setRange({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      });
    }
  }, [props.startedAt, props.endedAt]);

  const onChange = useCallback((item: RangeKeyDict) => {
    const selectionItem = item.selection;
    setRange({
      startDate: selectionItem.startDate,
      endDate: selectionItem.endDate,
      key: 'selection',
    });
    setIsSelected(true);
  }, []);
  const onClick = useCallback((e: SyntheticEvent) => {
    setAnchor(containerRef.current);
  }, []);
  const onChangeText = useCallback((e: SyntheticEvent) => {}, []);
  const onClickClear = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      setIsSelected(false);
      setAnchor(null);
      props.onChange(undefined, undefined);
    },
    [props.onChange],
  );
  const onClose = useCallback(() => {
    if (isSelected) {
      props.onChange(range.startDate, range.endDate);
    } else {
      props.onChange(undefined, undefined);
    }
    setAnchor(null);
  }, [props.onChange, isSelected, range]);

  return (
    <Container>
      <StyledTextField
        size={props.size || 'small'}
        value={
          props.startedAt && props.endedAt
            ? `${formatToYMD(props.startedAt, props.timeZone, {
                delimiter: '/',
              })} - ${formatToYMD(props.endedAt, props.timeZone, {
                delimiter: '/',
              })}`
            : props.placeholder
        }
        hasValue={!!props.startedAt && !!props.endedAt}
        suffix={<StyledIcon name="menu-down" size={fontSize(24)} />}
        onClick={onClick}
        onChange={onChangeText}
        inputRef={containerRef}
        textFieldHeight={props.textFieldHeight}
        textFieldWidth={props.textFieldWidth}
      />
      {anchor && (
        <Popover
          onClose={onClose}
          anchor={anchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <CalendarContainer>
            <DateRange
              locale={props.locale === 'ja' ? ja : enUS}
              color={colorPalette.primary.www}
              rangeColors={[colorPalette.primary.www]}
              onChange={onChange}
              ranges={[range]}
              months={isTablet ? 1 : 2}
              direction="horizontal"
              moveRangeOnFirstSelection={false}
              showDateDisplay={false}
            />
            <ClearButtonContainer>
              <ClearButton onClick={onClickClear}>
                <Icon
                  name="chevron-right"
                  size={fontSize(20)}
                  color="primary"
                />
                {t('Clear')}
              </ClearButton>
            </ClearButtonContainer>
          </CalendarContainer>
        </Popover>
      )}
    </Container>
  );
});

export default Component;

const Container = styled.div``;
const StyledTextField = styled(TextField)<{
  hasValue?: boolean;
  textFieldHeight?: number;
  textFieldWidth?: number;
}>`
  cursor: pointer;
  width: ${({ hasValue, textFieldWidth }) =>
    textFieldWidth ? textFieldWidth : hasValue ? '224' : '80'}px;
  height: ${({ textFieldHeight }) => textFieldHeight ?? '34'}px;
  background-color: ${white};
  color: ${gray01};

  & .MuiInputBase-root.Mui-disabled {
    color: ${black};
  }
  & .MuiIconButton-root.Mui-disabled {
    color: ${black};
    background-color: initial;
  }
  & .MuiInputBase-input {
    padding-left: ${spacing(8)};
    font-size: ${fontSize(14)};
  }
  & .MuiOutlinedInput-adornedEnd {
    padding-right: ${spacing(8)};
  }
  & .MuiInputAdornment-positionEnd {
    margin-top: ${spacing(3)};
    margin-left: 0;
  }
`;

const CalendarContainer = styled.div`
  background-color: ${white};
  min-width: 600px;

  ${mediaMax.tablet} {
    min-width: 290px;
  }

  // ラッパー系
  .rdrCalendarWrapper {
    width: 100%;
    padding: ${spacing(8)};
    font-size: ${fontSize(13)};
    background-color: ${white};
  }
  .rdrDateDisplayWrapper {
    display: none;
  }
  .rdrMonthAndYearWrapper {
    height: initial;
    padding-top: 0px;
  }

  // 年月ピッカー
  .rdrMonthPicker,
  .rdrYearPicker {
    position: relative;

    :after {
      position: absolute;
      right: 4px;
      top: 2px;
      content: 'arrow_drop_down';
      font-family: 'Material Icons';
      font-size: ${fontSize(18)};
      color: ${gray01};
      width: 18px;
      height: 18px;
    }
  }
  .rdrMonthAndYearPickers select {
    color: ${gray01};
    background: none;
  }
  .rdrMonthAndYearPickers select:hover {
    background-color: ${gray04};
  }

  // ボタン系
  .rdrNextPrevButton {
    background: ${blueGrayPale};
  }
  .rdrPprevButton i {
    border-right-color: ${gray01};
  }
  .rdrNextButton i {
    border-left-color: ${gray01};
  }

  // カレンダー枠
  .rdrMonth {
    width: 100%;
    padding: 0px;
  }
  .rdrMonth:last-of-type {
    margin-left: ${spacing(16)};

    ${mediaMax.tablet} {
      margin-left: 0;
    }
  }
  .rdrMonthName {
    ${mediaMax.tablet} {
      display: none;
    }
  }
  .rdrWeekDay {
    color: ${gray02};
  }

  // 日付表示
  .rdrDay {
    line-height: 3.6em;
    height: 3.6em;
  }
  .rdrDayNumber span {
    color: ${black};
    font-weight: normal;
  }
  .rdrDayPassive span {
    color: ${gray03};
  }

  .rdrDayStartOfMonth .rdrInRange,
  .rdrDayStartOfMonth .rdrEndEdge,
  .rdrDayStartOfWeek .rdrInRange,
  .rdrDayStartOfWeek .rdrEndEdge {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }
  .rdrDayEndOfMonth .rdrInRange,
  .rdrDayEndOfMonth .rdrStartEdge,
  .rdrDayEndOfWeek .rdrInRange,
  .rdrDayEndOfWeek .rdrStartEdge {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }
  .rdrDayStartOfMonth .rdrDayInPreview,
  .rdrDayStartOfMonth .rdrDayEndPreview,
  .rdrDayStartOfWeek .rdrDayInPreview,
  .rdrDayStartOfWeek .rdrDayEndPreview {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }
  .rdrDayEndOfMonth .rdrDayInPreview,
  .rdrDayEndOfMonth .rdrDayStartPreview,
  .rdrDayEndOfWeek .rdrDayInPreview,
  .rdrDayEndOfWeek .rdrDayStartPreview {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }
  .rdrStartEdge {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }
  .rdrEndEdge {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }
  .rdrCalendarWrapper:not(.rdrDateRangeWrapper)
    .rdrDayHovered
    .rdrDayNumber:after {
    border-radius: 50%;
  }

  // プレビュー
  .rdrDayStartPreview,
  .rdrDayInPreview,
  .rdrDayEndPreview {
    top: 5px;
    bottom: 5px;
  }
  .rdrDayStartPreview {
    left: 1px;
  }
  .rdrDayEndPreview {
    right: 2px;
  }
  .rdrDayStartPreview.rdrDayEndPreview {
    left: 1px;
    right: 2px;
  }
  .rdrDayStartPreview {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }
  .rdrDayEndPreview {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }

  // today
  .rdrDayToday .rdrDayNumber span {
    font-weight: ${fontWeight('bold')};
  }
  .rdrDayToday .rdrDayNumber span:after {
    bottom: 6px;
    background: ${primary};
  }
`;

const ClearButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing(12)};
`;
const ClearButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${black};
  font-size: ${fontSize(13)};
`;

const StyledIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
`;
